import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { subscribeWithSelector } from "zustand/middleware";
import { v4 as uuidv4 } from "uuid";
import sqliteStorage from "./sqliteStorage";
import { trace } from "../utils/debugTrace";

const generateId = () => uuidv4().replace(/-/g, "").toUpperCase();

const EMPTY_CART = {
  items: [],
  couponInput: [],
  advancePayment: 0,
  total: 0,
  tax: 0,
  discount: 0,
  paid: 0,
  change: 0,
  totalQty: 0,
  roundOff: 0,
  payments: [],
  redemptionPoints: 0,
  accumulationPoints: 0,
  creditAmount: 0,
  docType: "Invoice",
  sOrderID: "",
  referenceId: "",
  giftCardRefId: "",
  couponRefId: "",
  customer: null,
  salesRepId: null,
  cardPaymentData: {},
  documentno: "",
  userName: "",
  tillName: "",
  subTotal: 0,
  itemsSummery: {},
  amountInWords: "",
  groupedItemsByTaxRate: {},
  serviceCharge: 0,
  tip: 0,
  deiveryCharge: 0,
  packingCharge: 0,
  others: 0,
  refundAmount: 0,
  totalStd: 0,
  layAway: "N",
};

const useCartStore = create(
  subscribeWithSelector(
    persist(
      (set, get) => ({
        // State
        cart: { ...EMPTY_CART },
        selectedProductInCart: {},
        exchangeCart: null,
        _hydrated: false,

        // Actions — setCart supports functional updater like useState: setCart(prev => ({...prev, field}))
        // Always merges with EMPTY_CART to ensure array/object fields (payments, items, etc.) are never undefined
        setCart: (cartOrFn) => {
          const prevLen = get().cart?.items?.length || 0;
          if (typeof cartOrFn === "function") {
            set((state) => ({ cart: { ...EMPTY_CART, ...cartOrFn(state.cart) } }));
          } else {
            set({ cart: { ...EMPTY_CART, ...cartOrFn } });
          }
          const nextLen = get().cart?.items?.length || 0;
          if (prevLen !== nextLen || nextLen === 0) {
            trace("CartDiag", "setCart", { prevLen, nextLen, sOrderID: get().cart?.sOrderID, docno: get().cart?.documentno, stack: new Error().stack?.split("\n").slice(1, 5).join(" | ") });
          }
        },

        setSelectedProductInCart: (product) => set({ selectedProductInCart: product }),

        setExchangeCart: (cartOrFn) => {
          if (typeof cartOrFn === "function") {
            set((state) => ({ exchangeCart: cartOrFn(state.exchangeCart) }));
          } else {
            set({ exchangeCart: cartOrFn });
          }
        },

        addItem: (item) =>
          set((state) => {
            const items = [...state.cart.items, item];
            const total = items.reduce((sum, i) => sum + (i.nettotal || 0), 0);
            const totalQty = items.reduce((sum, i) => sum + Math.abs(i.weight || 0), 0);
            const tax = items.reduce((sum, i) => sum + (i.taxAmount || 0), 0);
            return {
              cart: { ...state.cart, items, total, totalQty, tax },
            };
          }),

        removeItem: (lineId) =>
          set((state) => {
            const items = state.cart.items.filter((i) => i.lineId !== lineId);
            const total = items.reduce((sum, i) => sum + (i.nettotal || 0), 0);
            const totalQty = items.reduce((sum, i) => sum + Math.abs(i.weight || 0), 0);
            const tax = items.reduce((sum, i) => sum + (i.taxAmount || 0), 0);
            return {
              cart: { ...state.cart, items, total, totalQty, tax },
              selectedProductInCart: {},
            };
          }),

        updateItemQty: (lineId, newQty) =>
          set((state) => {
            const items = state.cart.items.map((item) => {
              if (item.lineId !== lineId) return item;
              const weight = newQty;
              const nettotal = item.salePrice * weight;
              const taxAmount =
                item.taxRate > 0
                  ? item.taxFlag === "Y"
                    ? (nettotal * item.taxRate) / 100
                    : nettotal - nettotal / (1 + item.taxRate / 100)
                  : 0;
              return { ...item, weight, nettotal, total: nettotal, taxAmount };
            });
            const total = items.reduce((sum, i) => sum + (i.nettotal || 0), 0);
            const totalQty = items.reduce((sum, i) => sum + Math.abs(i.weight || 0), 0);
            const tax = items.reduce((sum, i) => sum + (i.taxAmount || 0), 0);
            return { cart: { ...state.cart, items, total, totalQty, tax } };
          }),

        clearCart: (preserveFields = {}) => {
          trace("CartDiag", "clearCart", { prevLen: get().cart?.items?.length || 0, stack: new Error().stack?.split("\n").slice(1, 5).join(" | ") });
          set({
            cart: {
              ...EMPTY_CART,
              sOrderID: generateId(),
              referenceId: generateId(),
              giftCardRefId: generateId(),
              couponRefId: generateId(),
              ...preserveFields,
            },
            selectedProductInCart: {},
          });
        },

        // Derived selectors
        getTotal: () => get().cart.total || 0,
        getItemCount: () => get().cart.items?.length || 0,
        getTotalQty: () => get().cart.totalQty || 0,
      }),
      {
        name: "cartObj",
        storage: createJSONStorage(() => sqliteStorage),
        skipHydration: true,
        partialize: (state) => ({ cart: state.cart, exchangeCart: state.exchangeCart }),
        merge: (persisted, current) => {
          const cart = persisted?.cart ? { ...EMPTY_CART, ...persisted.cart } : current.cart;
          // Stale identity guard: sqliteStorage debounces writes by 100ms, so a
          // reload can restore a pre-checkout snapshot whose sOrderID was already
          // consumed by a saved order. Reusing it overwrites that order locally
          // (db.orders is keyed on sOrderID) and gets silently deduped by the
          // backend, losing the new bill server-side. An empty cart has nothing
          // bound to its identity, so always reissue; non-empty carts are checked
          // against db.orders on POS mount (merge is sync — it cannot query).
          if (!cart.items?.length && !cart.payments?.length) {
            cart.sOrderID = generateId();
            cart.referenceId = generateId();
            cart.giftCardRefId = generateId();
            cart.couponRefId = generateId();
            // BUG-2072: an empty cart's documentno preview can carry a prior store's
            // till prefix across an Unlink Till + store switch (till.js re-hydrates this
            // store on every /till mount, but nothing else invalidates the stale preview).
            // Clearing it here forces Core/index.js's mount guard to regenerate it from
            // the newly selected store's tillData.prefix instead of reusing the stale one.
            cart.documentno = "";
          }
          // Ensure cart always has valid IDs (first open / web mode starts with empty IDs)
          if (!cart.sOrderID) cart.sOrderID = generateId();
          if (!cart.referenceId) cart.referenceId = generateId();
          if (!cart.giftCardRefId) cart.giftCardRefId = generateId();
          if (!cart.couponRefId) cart.couponRefId = generateId();
          // documentno is set by Core/index.js on POS mount (needs tillData + documentSequence)
          return {
            ...current,
            cart,
            exchangeCart: persisted?.exchangeCart ?? current.exchangeCart,
            _hydrated: true,
          };
        },
      }
    )
  )
);

export { EMPTY_CART };
export default useCartStore;
